import { ArrowLeft } from 'lucide-react';
import type { ReactElement } from 'react';
import {
  GroupPropertiesPanel,
  ImagePropertiesPanel,
  TextPropertiesPanel,
  updateNode,
} from '@/features/design-editor';
import { EyebrowLabel } from '@/shared/ui';
import type { CustomizeEditorReady } from '../model/use-customize-editor';

interface SelectedLayerSectionProps {
  readonly editor: CustomizeEditorReady;
  /** 텍스트 내용 칸 포커스 요청 순번 — 바뀔 때마다 내용 칸으로 포커스를 옮긴다. */
  readonly textFocusSeq: number | null;
}

const TITLES = { text: '폰트 스타일', image: '이미지', group: '그룹' } as const;

/**
 * 레이어를 고르면 오른쪽 패널 자리를 차지하는 스타일 패널 — 맨 위 [←](선택 해제, 상품 정보로) 와 제목,
 * 그 아래 텍스트(폰트 스타일)·이미지·그룹 속성. 선택이 없으면 그리지 않는다. Esc 로도 돌아간다(패널이 받는다).
 */
export const SelectedLayerSection = ({
  editor,
  textFocusSeq,
}: SelectedLayerSectionProps): ReactElement | null => {
  const { selected, store, textOptions, actions } = editor;
  if (selected === null) return null;

  return (
    <section aria-label="선택한 레이어" className="space-y-5">
      <div className="flex items-center gap-3 border-b border-foreground/10 pb-4">
        <button
          type="button"
          aria-label="상품 정보로 돌아가기 (Esc)"
          title="상품 정보로 돌아가기 (Esc)"
          className="inline-flex size-9 shrink-0 cursor-pointer items-center justify-center border border-foreground/15 transition-colors hover:border-foreground hover:bg-primary hover:text-primary-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={store.clearSelection}
        >
          <ArrowLeft aria-hidden className="size-4" strokeWidth={1.5} />
        </button>
        <div className="space-y-1">
          <EyebrowLabel>Selected</EyebrowLabel>
          <h2 className="text-lg font-bold">{TITLES[selected.type]}</h2>
        </div>
      </div>
      {selected.type === 'text' && (
        <TextPropertiesPanel
          node={selected}
          fonts={textOptions.fonts}
          inks={textOptions.inkColors}
          focusSeq={textFocusSeq}
          onChange={actions.patchText}
          onDelete={store.removeSelected}
        />
      )}
      {selected.type === 'image' && (
        <ImagePropertiesPanel
          node={selected}
          onChange={(patch) =>
            store.mutate((doc) =>
              updateNode(doc, selected.id, (node) =>
                node.type === 'image' ? { ...node, ...patch } : node,
              ),
            )
          }
          onDelete={store.removeSelected}
        />
      )}
      {selected.type === 'group' && <GroupPropertiesPanel onDelete={store.removeSelected} />}
    </section>
  );
};
